import React, { useEffect, useState } from 'react';
import { Typography, Paper, LinearProgress, Tooltip, Box } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { getStatusLimits } from '../../services/api';
import { UserProfile } from '../../types/UserProfile';

interface StatusLimit {
  status: number;
  name: string;
  baseLimit: number;
  extensionLimit: number;
  current: number;
}

interface StatusLimitsDisplayProps {
  profile: UserProfile;
}

const StatusLimitsDisplay: React.FC<StatusLimitsDisplayProps> = ({ profile }) => {
  const { t } = useTranslation('settings');
  const [statusLimits, setStatusLimits] = useState<StatusLimit[]>([]);

  useEffect(() => {
    const fetchStatusLimits = async () => {
      try {
        const limits = await getStatusLimits();
        const limitsWithCurrent = limits.map(limit => ({
          ...limit,
          current: profile.status_counts[limit.status - 1] || 0
        }));
        setStatusLimits(limitsWithCurrent);
      } catch (error) {
        console.error('ステータス制限の取得に失敗しました', error);
      }
    };

    fetchStatusLimits();
  }, [profile]);

  return (
    <Box sx={{ width: '100%', maxWidth: 600 }}>
      <Typography variant="h6" gutterBottom>
        {t('statusLimits.title')}
      </Typography>
      <Paper elevation={0} variant="outlined" sx={{ borderRadius: 2, px: 2, py: 0.5 }}>
        {statusLimits.map((limit, i) => {
          const totalLimit = limit.baseLimit + limit.extensionLimit;
          const pct = totalLimit > 0 ? Math.min((limit.current / totalLimit) * 100, 100) : 0;
          const near = pct >= 90;
          const breakdown = limit.extensionLimit > 0
            ? t('statusLimits.breakdownExt', { base: limit.baseLimit, ext: limit.extensionLimit })
            : t('statusLimits.breakdownBase', { total: totalLimit });
          return (
            <Box
              key={limit.status}
              sx={{
                py: 1.25,
                borderBottom: i < statusLimits.length - 1 ? '1px solid' : 'none',
                borderColor: 'divider',
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', mb: 0.5 }}>
                <Typography variant="caption" sx={{ fontWeight: 500 }}>
                  {limit.name}
                </Typography>
                <Typography variant="caption" sx={{ color: near ? 'warning.main' : 'text.secondary' }}>
                  <Box component="span" sx={{ fontWeight: 600, color: near ? 'warning.main' : 'text.primary' }}>
                    {limit.current.toLocaleString()}
                  </Box>
                  {' / '}{totalLimit.toLocaleString()}
                  {limit.extensionLimit > 0 && (
                    <Box component="span" sx={{ ml: 0.5, fontWeight: 600, color: 'text.secondary' }}>
                      (+{limit.extensionLimit.toLocaleString()})
                    </Box>
                  )}
                </Typography>
              </Box>
              <Tooltip title={breakdown} arrow placement="top">
                <LinearProgress
                  variant="determinate"
                  value={pct}
                  color={near ? 'warning' : 'primary'}
                  sx={{ height: 6, borderRadius: 3 }}
                />
              </Tooltip>
            </Box>
          );
        })}
      </Paper>
    </Box>
  );
};

export default StatusLimitsDisplay;
